import React, { useEffect, useState } from 'react';
import FaiTypography from "../../MuiComponents/FaiTypography";
import FaiBox from "../../MuiComponents/FaiBox";
import FaiButton from "../../MuiComponents/FaiButton";
import theme from "../../assets/theme";
import { Grid, CircularProgress } from "@mui/material";
import { format } from 'date-fns';
import OrdersController from '../../controllers/orderController';
import PaymentController from '../../controllers/paymentController';

// Trigger a client-side CSV download from rows of strings.
const downloadCSV = (rows: (string | number)[][], filename: string) => {
    const csv = rows
        .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const fmt = (n: any) => `Rs. ${Number(n ?? 0).toLocaleString()}`;

function SummaryCard({ bgColor, title, amount, sub }: any) {
    return (
        <FaiBox p={3} sx={{ borderRadius: 2, backgroundColor: bgColor, color: '#fff', minHeight: 110 }}>
            <FaiTypography variant={"h6"} color={"inherit"}>{title}</FaiTypography>
            <FaiTypography variant={"h4"} fontWeight={"bold"} color={"inherit"}>{amount}</FaiTypography>
            {sub != null && <FaiTypography variant={"caption"} color={"inherit"}>{sub}</FaiTypography>}
        </FaiBox>
    );
}

const Reports: React.FC = () => {
    const { typography } = theme;
    const [loading, setLoading] = useState(true);
    const [dash, setDash] = useState<any>(null);
    const [calc, setCalc] = useState<any>(null);
    const currentMonth = format(new Date(), 'yyyy-MM');

    useEffect(() => {
        const run = async () => {
            try {
                setLoading(true);
                const [dashRes, calcRes] = await Promise.all([
                    OrdersController.getMerchantDashboardSummary(),
                    PaymentController.getPaymentCalculationSummary(currentMonth),
                ]);
                setDash(dashRes.data.data);
                setCalc(calcRes.data.data);
            } catch (e) {
                console.error('Error loading reports:', e);
            } finally {
                setLoading(false);
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const orderCard = (label: string, bucket: any, bgColor: string) => (
        <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
                bgColor={bgColor}
                title={label}
                amount={fmt(bucket?.amount)}
                sub={`${bucket?.count ?? 0} order${(bucket?.count ?? 0) === 1 ? '' : 's'}`}
            />
        </Grid>
    );

    const handleDownload = () => {
        const rows: (string | number)[][] = [
            ['Report', `Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
            [],
            ['Metric', 'Count', 'Amount (Rs.)'],
            ["Today's orders", dash?.today?.count ?? 0, dash?.today?.amount ?? 0],
            ["Yesterday's orders", dash?.yesterday?.count ?? 0, dash?.yesterday?.amount ?? 0],
            ['This month orders', dash?.thisMonth?.count ?? 0, dash?.thisMonth?.amount ?? 0],
            ['All-time sales', dash?.allTime?.count ?? 0, dash?.allTime?.amount ?? 0],
            ['Pending orders', dash?.pendingOrdersCount ?? 0, ''],
            [],
            [`This month (${currentMonth})`, 'Count', 'Amount (Rs.)'],
            ['Delivered orders', calc?.deliveredOrdersCount ?? 0, calc?.deliveredOrdersAmount ?? 0],
            ['Returned orders', calc?.returnedOrdersCount ?? 0, calc?.returnedOrdersAmount ?? 0],
            ['Outstanding dues', '', calc?.outstandingDues ?? 0],
            ['Next payment', '', calc?.nextPayment ?? 0],
        ];
        downloadCSV(rows, `merchant-report-${currentMonth}.csv`);
    };

    if (loading) {
        return (
            <FaiBox display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress />
            </FaiBox>
        );
    }

    return (
        <FaiBox className={"reports"}>
            <FaiBox display="flex" justifyContent="space-between" alignItems="center" mb={"21px"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
                    Reports
                </FaiTypography>
                <FaiButton color="primary" onClick={handleDownload}>Download CSV</FaiButton>
            </FaiBox>

            <Grid container spacing={3}>
                {orderCard("Today's Orders", dash?.today, "#1AB595")}
                {orderCard("Yesterday's Orders", dash?.yesterday, "#3190D0")}
                {orderCard("This Month", dash?.thisMonth, "#10459E")}
                {orderCard("All-Time Sales", dash?.allTime, "#E99956")}
            </Grid>

            <FaiTypography variant={"h5"} fontWeight={typography.fontWeightMedium} mt={4} mb={2}>
                This Month ({format(new Date(), 'MMMM yyyy')})
            </FaiTypography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard bgColor={"#00682A"} title={"Delivered"} amount={fmt(calc?.deliveredOrdersAmount)} sub={`${calc?.deliveredOrdersCount ?? 0} orders`} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard bgColor={"#FF6A6A"} title={"Returned"} amount={fmt(calc?.returnedOrdersAmount)} sub={`${calc?.returnedOrdersCount ?? 0} orders`} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard bgColor={"#8E44AD"} title={"Next Payment"} amount={fmt(calc?.nextPayment)} sub={null} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard bgColor={"#C0392B"} title={"Outstanding"} amount={fmt(calc?.outstandingDues)} sub={null} />
                </Grid>
            </Grid>
        </FaiBox>
    );
};

export default Reports;
